// Productpagina: galerij (swipe, stipjes, pijlen, miniaturen) en de koopbalk onderin.

class ProductGallery extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-track]');
    if (!this.track) return;
    this.slides = [...this.track.querySelectorAll('[data-slide]')];
    this.dots = [...this.querySelectorAll('.gallery__dots i')];
    this.thumbs = [...this.querySelectorAll('[data-thumb]')];
    this.prev = this.querySelector('[data-prev]');
    this.next = this.querySelector('[data-next]');
    this.track.addEventListener('scroll', () => requestAnimationFrame(() => this.update()), { passive: true });
    this.prev?.addEventListener('click', () => this.go(this.index - 1));
    this.next?.addEventListener('click', () => this.go(this.index + 1));
    this.thumbs.forEach((thumb) => thumb.addEventListener('click', () => this.go(Number(thumb.dataset.thumb))));
    this.setupThumbStrip();
    this.index = 0;
    this.update();
    this.setupLightbox();
  }

  // Rij miniaturen: past hij niet, dan scrollt hij (muiswiel, trackpad of schuifbalk), met een vervaging aan de
  // kant waar nog foto's staan. Past hij wel, dan geen schuifbalk. De actieve foto schuift vanzelf in beeld.
  setupThumbStrip() {
    this.strip = this.querySelector('[data-thumb-strip]');
    if (!this.strip) return;
    const edges = () => {
      const max = this.strip.scrollWidth - this.strip.clientWidth;
      this.strip.classList.toggle('is-scrollable', max > 2);
      this.strip.classList.toggle('is-more-left', this.strip.scrollLeft > 2);
      this.strip.classList.toggle('is-more-right', this.strip.scrollLeft < max - 2);
    };
    this.strip.addEventListener('scroll', () => requestAnimationFrame(edges), { passive: true });
    this.strip.addEventListener(
      'wheel',
      (event) => {
        if (this.strip.scrollWidth <= this.strip.clientWidth || Math.abs(event.deltaX) > Math.abs(event.deltaY)) return;
        event.preventDefault();
        this.strip.scrollLeft += event.deltaY;
      },
      { passive: false },
    );
    new ResizeObserver(edges).observe(this.strip);
    edges();
  }

  showThumb(index) {
    const thumb = this.thumbs[index];
    if (!this.strip || !thumb || this.strip.scrollWidth <= this.strip.clientWidth) return;
    const item = thumb.closest('li') || thumb;
    const left = item.offsetLeft - this.strip.offsetLeft;
    const right = left + item.offsetWidth;
    const view = this.strip.scrollLeft;
    const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
    const behavior = reduce ? 'auto' : 'smooth';
    if (left < view + 24) this.strip.scrollTo({ left: Math.max(0, left - 24), behavior });
    else if (right > view + this.strip.clientWidth - 24) this.strip.scrollTo({ left: right - this.strip.clientWidth + 24, behavior });
  }

  // Groot bekijken: een klik op een foto opent alle productfoto's boven de site. Swipen, pijlen, miniaturen of de
  // pijltjestoetsen; Esc, het kruisje of een klik naast de foto sluit. Daarna staat de galerij op dezelfde foto.
  setupLightbox() {
    const dialog = this.querySelector('[data-lightbox]');
    if (!(dialog instanceof HTMLDialogElement)) return;
    this.lightbox = dialog;
    this.lbTrack = dialog.querySelector('[data-lightbox-track]');
    this.lbSlides = [...dialog.querySelectorAll('[data-lightbox-slide]')];
    this.lbThumbs = [...dialog.querySelectorAll('[data-lightbox-thumb]')];
    this.lbPrev = dialog.querySelector('[data-lightbox-prev]');
    this.lbNext = dialog.querySelector('[data-lightbox-next]');
    this.lbIndex = 0;

    this.querySelectorAll('[data-open-image]').forEach((button) => {
      button.addEventListener('click', () => this.openLightbox(Number(button.dataset.openImage)));
    });
    dialog.querySelector('[data-lightbox-close]')?.addEventListener('click', () => dialog.close());
    this.lbPrev?.addEventListener('click', () => this.lightboxGo(this.lbIndex - 1));
    this.lbNext?.addEventListener('click', () => this.lightboxGo(this.lbIndex + 1));
    this.lbTrack.addEventListener('scroll', () => requestAnimationFrame(() => this.lightboxUpdate()), { passive: true });
    this.lbThumbs.forEach((thumb) => thumb.addEventListener('click', () => this.lightboxGo(Number(thumb.dataset.lightboxThumb))));
    // Klik naast de foto (op de lege ruimte van de dia) sluit het venster.
    this.lbTrack.addEventListener('click', (event) => {
      if (event.target.closest('[data-lightbox-slide]') && !(event.target instanceof HTMLImageElement)) dialog.close();
    });
    // preventDefault: anders schuift de browser de strook zelf ook een stap en ga je er twee vooruit.
    dialog.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      this.lightboxGo(this.lbIndex + (event.key === 'ArrowRight' ? 1 : -1));
    });
    dialog.addEventListener('close', () => {
      document.documentElement.classList.remove('lightbox-open');
      const slide = this.slides[Number(this.lbSlides[this.lbIndex]?.dataset.mediaIndex)];
      if (slide) this.track.scrollTo({ left: slide.offsetLeft - this.track.offsetLeft, behavior: 'auto' });
    });
  }

  openLightbox(index) {
    document.documentElement.classList.add('lightbox-open');
    // Miniaturen meteen laden zodra het venster opengaat (niet eerder: dan laadt elke productpagina ze mee).
    this.lightbox.querySelectorAll('.lightbox__thumbs img[loading="lazy"]').forEach((img) => (img.loading = 'eager'));
    this.lightbox.showModal();
    this.lbIndex = Math.max(0, Math.min(index, this.lbSlides.length - 1));
    this.lbTrack.scrollTo({ left: this.lbIndex * this.lbTrack.clientWidth, behavior: 'auto' });
    this.lightboxUpdate();
  }

  lightboxGo(index) {
    const target = Math.max(0, Math.min(index, this.lbSlides.length - 1));
    const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.lbTrack.scrollTo({ left: target * this.lbTrack.clientWidth, behavior: reduce ? 'auto' : 'smooth' });
  }

  lightboxUpdate() {
    const width = this.lbTrack.clientWidth || 1;
    this.lbIndex = Math.round(this.lbTrack.scrollLeft / width);
    this.lbThumbs.forEach((thumb, i) => thumb.setAttribute('aria-current', String(i === this.lbIndex)));
    const active = this.lbThumbs[this.lbIndex]?.parentElement;
    const strip = active?.parentElement;
    if (active && strip && strip.scrollWidth > strip.clientWidth) {
      strip.scrollTo({ left: active.offsetLeft - (strip.clientWidth - active.offsetWidth) / 2, behavior: 'auto' });
    }
    if (this.lbPrev) this.lbPrev.disabled = this.lbIndex <= 0;
    if (this.lbNext) this.lbNext.disabled = this.lbIndex >= this.lbSlides.length - 1;
  }

  go(index) {
    const target = this.slides[Math.max(0, Math.min(index, this.slides.length - 1))];
    if (!target) return;
    const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.track.scrollTo({ left: target.offsetLeft - this.track.offsetLeft, behavior: reduce ? 'auto' : 'smooth' });
  }

  update() {
    const width = this.track.clientWidth || 1;
    this.index = Math.round(this.track.scrollLeft / width);
    this.dots.forEach((dot, i) => dot.classList.toggle('is-active', i === this.index));
    this.thumbs.forEach((thumb, i) => thumb.setAttribute('aria-current', String(i === this.index)));
    if (this.index !== this.shownThumb) {
      this.shownThumb = this.index;
      this.showThumb(this.index);
    }
    if (this.prev) this.prev.disabled = this.index <= 0;
    if (this.next) this.next.disabled = this.index >= this.slides.length - 1;
  }
}
customElements.define('product-gallery', ProductGallery);

class StickyBuyBar extends HTMLElement {
  connectedCallback() {
    const main = document.querySelector('[data-main-atc]');
    if (!main || !('IntersectionObserver' in window)) return;
    this.hidden = false;
    // Zichtbaar zodra de hoofdkoopknop niet in beeld is, ook als hij bij het openen nog onder de vouw staat
    // (advertentieverkeer op mobiel, docs/mobile-ads.md). Nooit twee koopknoppen tegelijk in beeld.
    this.observer = new IntersectionObserver(([entry]) => {
      this.classList.toggle('is-visible', !entry.isIntersecting);
    });
    this.observer.observe(main);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }
}
customElements.define('sticky-buy-bar', StickyBuyBar);

// Reviews (B54): onze knop "Schrijf een review" opent het formulier van Judge.me. De kop van Judge.me, met zijn eigen
// knop, is verborgen; die knop klikken we hier aan.
document.addEventListener('click', (event) => {
  if (!event.target.closest('[data-write-review]')) return;
  document.querySelector('#reviews .jm-action-buttons__button, #reviews .jdgm-write-rev-link')?.click();
});
